/* eslint-disable @typescript-eslint/no-empty-function */
import { FC, useEffect, useRef, useState } from 'react';

import { Button, Divider, Drawer as MuiDrawer, Typography } from '@mui/material';
import clsx from 'clsx';
import { useDrawerStyles } from './Drawer.styles';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { AlignTypes } from 'models/commonTypes';

type DrawerCustomClassesType = {
  root?: string;
  button?: string;
};

export interface DrawerProps {
  /** Determines on which side the drawer will appear. */
  anchor?: 'left' | 'right';

  /** Make Drawer customizable */
  customClasses?: DrawerCustomClassesType;

  /** Defines the maximum width of the Drawer */
  maxWidth?: string | number;

  /** Defines the minimum width of the open state of the Drawer */
  minWidth?: number;

  /** Callback executed when the user stops resizing the sidebar */
  onResizeEnd?: (newWidth: number, isOpen: boolean) => void;

  /** Determines whether the drawer should be rendered collapsed*/
  open?: boolean;

  /** Defines if the user is allowed to resize the drawer */
  resizable?: boolean;

  /** Title for the header of the drawer */
  title?: string;

  /** Function to control the open state of drawer */
  toggleOpen?: (width: number) => void;

  /** The width of the drawer in its *open* state */
  width?: number;

  children?: React.ReactNode;
}

const COLLAPSED_WIDTH = 26;

function debounce(fn: () => void, ms: number) {
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn();
    }, ms);
  };
}

export const Drawer: FC<DrawerProps> = ({
  anchor = 'left',
  maxWidth = 'none',
  minWidth = 0,
  // eslint-disable-next-line no-empty-function
  onResizeEnd = () => {},
  open = true,
  resizable = false,
  title = '',
  customClasses = {},
  width = 240,
  // eslint-disable-next-line no-empty-function
  toggleOpen = () => {},
  children,
}) => {
  const [isResizing, setResizing] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(width);

  const drawerMinWidth = Math.min(Math.max(COLLAPSED_WIDTH, minWidth), width);
  const anchorLeft = anchor === AlignTypes.LEFT;
  const isOpen = open;
  const rootRef = useRef(null);
  const classes = useDrawerStyles();

  const handleDrawerClose = () => {
    toggleOpen(currentWidth);
  };

  const renderToggleIcon = () => {
    if ((anchorLeft && open) || (!anchorLeft && !open)) {
      return <ChevronLeft />;
    }

    return <ChevronRight />;
  };

  let currentX = 0;

  const onStartResize = e => {
    e.preventDefault();
    currentX = e.pageX;
    window.addEventListener('mousemove', onResize);
    window.addEventListener('mouseup', onStopResize);

    setResizing(true);
  };

  const onResize = e => {
    const rw = getResizeWidth(e.pageX);
    setCurrentWidth(rw);
  };

  const onStopResize = () => {
    const newWidth = rootRef.current.offsetWidth;

    setResizing(false);
    window.removeEventListener('mousemove', onResize);
    window.removeEventListener('mouseup', onStopResize);

    if (newWidth < drawerMinWidth) {
      handleDrawerClose();
      setCurrentWidth(drawerMinWidth);
      onResizeEnd(COLLAPSED_WIDTH, false);
    } else {
      setCurrentWidth(newWidth);
      onResizeEnd(newWidth, true);
    }
  };

  const getResizeWidth = (pageX: number) =>
    anchorLeft
      ? Math.max(COLLAPSED_WIDTH, currentWidth + pageX - currentX)
      : Math.max(COLLAPSED_WIDTH, currentWidth + currentX - pageX);

  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      const newWidth = rootRef.current.offsetWidth;

      if (isOpen && newWidth !== currentWidth) {
        setCurrentWidth(newWidth);
        onResizeEnd(newWidth, open);
      }
    }, 1000);

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  });

  useEffect(() => {
    if (width < minWidth) {
      console.warn(`Incompatible properties width: ${width}px and minWidth: ${minWidth}px`);
    }

    setCurrentWidth(width);
  }, [minWidth, width]);

  return (
    <div
      className={clsx(
        classes.drawerWrapper,
        anchorLeft ? '' : classes.drawerRight,
        isOpen ? '' : classes.drawerWrapperClose,
        isResizing ? '' : classes.drawerAnimation,
        customClasses.root,
      )}
      ref={rootRef}
      style={{
        maxWidth,
        width: isOpen ? currentWidth : COLLAPSED_WIDTH,
      }}
    >
      <MuiDrawer
        anchor={anchor}
        classes={{
          root: clsx(classes.drawer, isResizing ? '' : classes.drawerAnimation),
          paper: clsx(
            classes.paper,
            isOpen ? '' : classes.drawerClose,
            isResizing ? '' : classes.drawerAnimation,
          ),
        }}
        sx={{ width: isOpen ? currentWidth : COLLAPSED_WIDTH }}
        variant="permanent"
        open={true}
        hideBackdrop
      >
        <Button
          className={clsx(
            classes.drawerButton,
            anchorLeft ? classes.drawerButtonLeft : classes.drawerButtonRight,
            customClasses.button,
          )}
          color="primary"
          disableRipple
          onClick={handleDrawerClose}
          onMouseOver={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          variant="contained"
          data-testid="drawer-toggle"
        >
          {renderToggleIcon()}
        </Button>
        {title && (
          <>
            <div
              className={clsx(
                classes.drawerHeader,
                anchorLeft ? classes.drawerHeaderLeft : classes.drawerHeaderRight,
              )}
            >
              <Typography
                className={clsx(classes.title, anchorLeft ? classes.titleLeft : classes.titleRight)}
              >
                {title}
              </Typography>
            </div>
            <Divider />
          </>
        )}
        {resizable && open && (
          <div
            className={clsx(
              classes.resizeControl,
              anchorLeft ? classes.resizeControlLeft : classes.resizeControlRight,
            )}
            onMouseDown={e => {
              onStartResize(e);
            }}
            onMouseOver={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <span
              className={
                isResizing
                  ? anchorLeft
                    ? classes.resizeIndicatorLeft
                    : classes.resizeIndicatorRight
                  : classes.hidden
              }
            />
          </div>
        )}
        {isOpen ? children : ''}
      </MuiDrawer>
    </div>
  );
};
